import type { CraneConfig } from '../../config/minigames.config';
import type { CraneGame } from '../../minigames/CraneGame';
import { AttemptGrade } from '../../minigames/MiniGame';
import { COLORS, Cached, drawBurst, drawGradeLabel, roundRect, seeded, shakeOffset, softShadow, steel, vignette } from './paint';

/** Brick pallet on the hook (m): width, height of pallet + bricks. */
const LOAD = { width: 1.3, pallet: 0.16, bricks: 0.62 } as const;
/** A set-down load stays this long on the slab (s), the last LANDED_FADE of it fading out. */
const LANDED_VISIBLE = 1.2;
const LANDED_FADE = 0.3;

/**
 * Crane, seen from the side in the evening light: the lattice jib across the top, the trolley running along it, rope
 * and hook block, a pallet of bricks in the chain sling. Below, the slab of a shell with the marked set-down spot –
 * yellow-black edge, the centre line for a perfect set-down.
 */
export class CranePainter {
  private readonly backdrop = new Cached();

  constructor(private readonly config: CraneConfig) {}

  paint(ctx: CanvasRenderingContext2D, w: number, h: number, game: CraneGame, t: number, scale: number): void {
    const c = this.config;
    const slabY = h * 0.84;
    const m = Math.min(w / 17, (slabY - h * 0.1) / (game.trolleyHeight + 0.4));
    const X = (x: number) => w / 2 + x * m;
    const Y = (y: number) => slabY - y * m;
    const jibY = Y(game.trolleyHeight) - 10;
    const shake = shakeOffset(game.secondsSinceGrade, game.grade === AttemptGrade.Miss ? 2 : 5, t);
    ctx.save();
    ctx.translate(shake.x, shake.y);
    ctx.drawImage(this.backdrop.get(w, h, scale, (b) => drawBackdrop(b, w, h, slabY, jibY, m, c)), 0, 0, w, h);

    // loads set down so far (the latest in front)
    // the load just set down stays about a second, then fades away (user 2026-10-04) – the slab is clear for the next
    const setDown = game.landed[game.landed.length - 1];
    const sinceLanding = game.secondsSinceGrade;
    if (setDown && Number.isFinite(setDown.x) && sinceLanding < LANDED_VISIBLE) {
      const squash = sinceLanding < 0.18 ? 1 - Math.sin((sinceLanding / 0.18) * Math.PI) * 0.12 : 1;
      const fade = Math.min(1, (LANDED_VISIBLE - sinceLanding) / LANDED_FADE);
      drawLoad(ctx, X(setDown.x), slabY, m, squash, fade);
    }

    // trolley and rope
    const tx = X(game.trolleyX);
    drawTrolley(ctx, tx, jibY, m);
    const load = game.load;
    if (load?.hanging) {
      const top = { x: tx, y: jibY + m * 0.55 };
      const hook = { x: X(load.x), y: Y(load.y + LOAD.pallet + LOAD.bricks) - m * 1.1 };
      ctx.strokeStyle = '#1b1f26';
      ctx.lineWidth = Math.max(1.5, m * 0.06);
      for (const off of [-0.12, 0.12]) {
        ctx.beginPath();
        ctx.moveTo(top.x + off * m, top.y);
        ctx.lineTo(hook.x + off * m * 0.6, hook.y);
        ctx.stroke();
      }
      drawHookAndSling(ctx, hook.x, hook.y, X(load.x), Y(load.y + LOAD.pallet + LOAD.bricks), m, game.swing);
    } else {
      // empty hook pulled up a little after the release
      const top = jibY + m * 0.55;
      const hy = top + m * (c.ropeLength - 1.6);
      ctx.strokeStyle = '#1b1f26';
      ctx.lineWidth = Math.max(1.5, m * 0.06);
      ctx.beginPath();
      ctx.moveTo(tx, top);
      ctx.lineTo(tx, hy);
      ctx.stroke();
      drawHookBlock(ctx, tx, hy, m);
    }
    // shadow of the load on the slab: tells where it is over the slab
    if (load) {
      const height = Math.max(0, load.y);
      softShadow(ctx, X(load.x), slabY + 2, (LOAD.width / 2) * m * (1 + height * 0.06), m * 0.18, 0.45 / (1 + height * 0.25));
    }
    if (load) {
      if (!load.hanging) {
        // motion streak behind the falling load
        ctx.save();
        ctx.globalAlpha = 0.18;
        drawLoad(ctx, X(load.x), Y(load.y) - m * 0.5, m, 1, 1);
        ctx.restore();
      }
      drawLoad(ctx, X(load.x), Y(load.y), m, 1, 1);
    }

    // landing dust and the verdict
    const last = game.landed[game.landed.length - 1];
    const age = game.secondsSinceGrade;
    if (last && age < 1.2) {
      if (Number.isFinite(last.x)) {
        const lx = X(last.x);
        drawBurst(ctx, { x: lx - LOAD.width * 0.5 * m, y: slabY, count: 14, speed: m * 3.2, gravity: m * 2, life: 0.9, colors: ['rgba(220,205,180,0.8)', 'rgba(190,175,150,0.7)'], spread: Math.PI * 0.42, size: m * 0.22, seed: game.landed.length * 31, streaks: false }, age);
        drawBurst(ctx, { x: lx + LOAD.width * 0.5 * m, y: slabY, count: 14, speed: m * 3.2, gravity: m * 2, life: 0.9, colors: ['rgba(220,205,180,0.8)', 'rgba(190,175,150,0.7)'], spread: Math.PI * 0.42, size: m * 0.22, seed: game.landed.length * 57, streaks: false }, age);
        if (last.grade === AttemptGrade.Perfect) drawBurst(ctx, { x: lx, y: slabY - m * 0.9, count: 26, speed: m * 7, gravity: m * 6, life: 0.8, colors: ['#fff7c2', COLORS.gold, '#ffb12b'], spread: Math.PI, size: 2, seed: game.landed.length * 97, streaks: true }, age);
      }
      const word = last.grade === AttemptGrade.Good ? `${Math.abs(last.x).toFixed(2).replace('.', ',')} m daneben` : !Number.isFinite(last.x) ? 'ZU SPÄT' : last.grade === AttemptGrade.Miss ? 'NEBEN DER MARKE' : undefined;
      drawGradeLabel(ctx, last.grade, age, w / 2, h * 0.36, Math.round(h * (last.grade === AttemptGrade.Good ? 0.085 : 0.11)), word);
    }
    vignette(ctx, w, h, 0.3);
    ctx.restore();
  }
}

function drawBackdrop(ctx: CanvasRenderingContext2D, w: number, h: number, slabY: number, jibY: number, m: number, c: CraneConfig): void {
  // evening sky
  const sky = ctx.createLinearGradient(0, 0, 0, slabY);
  sky.addColorStop(0, '#1d2b52');
  sky.addColorStop(0.45, '#5b5f8f');
  sky.addColorStop(0.78, '#f2a65a');
  sky.addColorStop(1, '#ffd08a');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, w, h);
  // sun low behind the city
  const sun = ctx.createRadialGradient(w * 0.78, slabY - h * 0.12, 0, w * 0.78, slabY - h * 0.12, h * 0.45);
  sun.addColorStop(0, 'rgba(255, 240, 200, 0.95)');
  sun.addColorStop(0.12, 'rgba(255, 210, 130, 0.6)');
  sun.addColorStop(1, 'rgba(255, 180, 90, 0)');
  ctx.fillStyle = sun;
  ctx.fillRect(0, 0, w, h);
  // city silhouette, two layers
  const rnd = seeded(5);
  for (const [base, tone, maxH] of [[slabY - h * 0.05, 'rgba(70, 70, 110, 0.55)', 0.3], [slabY - h * 0.02, 'rgba(40, 42, 70, 0.8)', 0.2]] as const) {
    ctx.fillStyle = tone;
    let x = -10;
    while (x < w + 10) {
      const bw = w * (0.035 + rnd() * 0.06);
      const bh = h * (0.05 + rnd() * maxH);
      ctx.fillRect(x, base - bh, bw, bh + h);
      // a few lit windows
      if (tone.includes('40')) {
        ctx.fillStyle = 'rgba(255, 214, 140, 0.55)';
        for (let i = 0; i < 4; i++) if (rnd() < 0.5) ctx.fillRect(x + 3 + rnd() * (bw - 8), base - bh + 4 + rnd() * (bh - 10), 2.5, 3);
        ctx.fillStyle = tone;
      }
      x += bw + w * 0.004;
    }
  }
  // tower of the crane on the left edge, the jib across
  const towerX = w * 0.06;
  drawLattice(ctx, towerX - m * 0.6, jibY, towerX + m * 0.6, slabY, true);
  drawLattice(ctx, -m, jibY - m * 0.9, w + m, jibY, false);
  // counter-jib cab
  ctx.fillStyle = '#e8b020';
  roundRect(ctx, towerX - m * 0.9, jibY - m * 1.9, m * 1.8, m * 1.0, 3);
  ctx.fill();
  ctx.fillStyle = 'rgba(160, 210, 255, 0.75)';
  ctx.fillRect(towerX - m * 0.7, jibY - m * 1.75, m * 1.4, m * 0.45);

  // the slab of the shell: concrete edge, columns below, the floor beyond
  const slabTop = ctx.createLinearGradient(0, slabY - 2, 0, slabY + m * 0.5);
  slabTop.addColorStop(0, '#d9d6cf');
  slabTop.addColorStop(1, '#9d9a93');
  ctx.fillStyle = slabTop;
  ctx.fillRect(0, slabY, w, m * 0.5);
  ctx.fillStyle = '#5f5d59';
  ctx.fillRect(0, slabY + m * 0.5, w, h);
  for (let x = w * 0.05; x < w; x += m * 4) {
    ctx.fillStyle = '#7b7974';
    ctx.fillRect(x, slabY + m * 0.5, m * 0.5, h);
  }
  ctx.fillStyle = 'rgba(20, 20, 28, 0.55)';
  ctx.fillRect(0, slabY + m * 0.5, w, m * 0.25);
  // rebar stubs at the far edges
  ctx.strokeStyle = '#7a3f22';
  ctx.lineWidth = 1.5;
  for (const x0 of [w * 0.03, w * 0.95]) for (let i = 0; i < 4; i++) {
    ctx.beginPath();
    ctx.moveTo(x0 + i * 4, slabY);
    ctx.lineTo(x0 + i * 4, slabY - m * 0.8);
    ctx.stroke();
  }

  // the target: yellow-black edge and centre line
  const half = c.targetHalfWidth * m;
  const cx = w / 2;
  ctx.save();
  ctx.beginPath();
  ctx.rect(cx - half, slabY - 1, half * 2, 5);
  ctx.clip();
  for (let x = cx - half - 12; x < cx + half + 12; x += 12) {
    ctx.fillStyle = '#ffd400';
    ctx.beginPath();
    ctx.moveTo(x, slabY + 5);
    ctx.lineTo(x + 6, slabY - 1);
    ctx.lineTo(x + 12, slabY - 1);
    ctx.lineTo(x + 6, slabY + 5);
    ctx.fill();
    ctx.fillStyle = '#16181c';
    ctx.beginPath();
    ctx.moveTo(x + 6, slabY + 5);
    ctx.lineTo(x + 12, slabY - 1);
    ctx.lineTo(x + 18, slabY - 1);
    ctx.lineTo(x + 12, slabY + 5);
    ctx.fill();
  }
  ctx.restore();
  // marker posts with the perfect window between them
  const perfect = c.perfectHalfWidth * m;
  ctx.fillStyle = COLORS.gold;
  ctx.fillRect(cx - perfect, slabY - 1, perfect * 2, 6);
  ctx.strokeStyle = 'rgba(255, 210, 90, 0.5)';
  ctx.setLineDash([3, 4]);
  ctx.beginPath();
  ctx.moveTo(cx, slabY);
  ctx.lineTo(cx, jibY + m);
  ctx.stroke();
  ctx.setLineDash([]);
  for (const side of [-1, 1]) {
    const px = cx + side * half;
    ctx.fillStyle = '#ffd400';
    ctx.fillRect(px - 1.5, slabY - m * 0.7, 3, m * 0.7);
    ctx.fillStyle = '#e0473c';
    ctx.beginPath();
    ctx.arc(px, slabY - m * 0.75, 3, 0, Math.PI * 2);
    ctx.fill();
  }
}

/** Yellow lattice girder between two corners (vertical tower or horizontal jib). */
function drawLattice(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, vertical: boolean): void {
  ctx.save();
  ctx.strokeStyle = '#e8b020';
  ctx.lineWidth = 2.2;
  ctx.lineCap = 'round';
  ctx.beginPath();
  if (vertical) {
    ctx.moveTo(x0, y0);
    ctx.lineTo(x0, y1);
    ctx.moveTo(x1, y0);
    ctx.lineTo(x1, y1);
    const step = (x1 - x0) * 1.2;
    let flip = false;
    for (let y = y0; y < y1; y += step) {
      ctx.moveTo(flip ? x1 : x0, y);
      ctx.lineTo(flip ? x0 : x1, y + step);
      flip = !flip;
    }
  } else {
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y0);
    ctx.moveTo(x0, y1);
    ctx.lineTo(x1, y1);
    const step = (y1 - y0) * 1.1;
    let flip = false;
    for (let x = x0; x < x1; x += step) {
      ctx.moveTo(x, flip ? y1 : y0);
      ctx.lineTo(x + step, flip ? y0 : y1);
      flip = !flip;
    }
  }
  ctx.stroke();
  ctx.strokeStyle = 'rgba(80, 50, 0, 0.35)';
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();
}

function drawTrolley(ctx: CanvasRenderingContext2D, x: number, y: number, m: number): void {
  ctx.fillStyle = '#2b3240';
  roundRect(ctx, x - m * 0.6, y - m * 0.15, m * 1.2, m * 0.6, 3);
  ctx.fill();
  ctx.fillStyle = COLORS.orange;
  ctx.fillRect(x - m * 0.6, y + m * 0.1, m * 1.2, m * 0.12);
  ctx.fillStyle = '#11151c';
  for (const side of [-0.38, 0.38]) {
    ctx.beginPath();
    ctx.arc(x + side * m, y - m * 0.18, m * 0.14, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawHookBlock(ctx: CanvasRenderingContext2D, x: number, y: number, m: number): void {
  ctx.fillStyle = '#f2c230';
  roundRect(ctx, x - m * 0.3, y, m * 0.6, m * 0.55, 4);
  ctx.fill();
  ctx.fillStyle = '#16181c';
  ctx.fillRect(x - m * 0.3, y + m * 0.22, m * 0.6, m * 0.08);
  ctx.strokeStyle = steel(ctx, x - m * 0.2, x + m * 0.2);
  ctx.lineWidth = m * 0.11;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x, y + m * 0.55);
  ctx.lineTo(x, y + m * 0.75);
  ctx.arc(x - m * 0.17, y + m * 0.75, m * 0.17, 0, Math.PI * 0.85);
  ctx.stroke();
}

function drawHookAndSling(ctx: CanvasRenderingContext2D, hx: number, hy: number, lx: number, loadTop: number, m: number, swing: number): void {
  drawHookBlock(ctx, hx, hy - m * 0.1, m);
  // four chain legs to the pallet corners (two visible pairs), rotating with the swing
  const ringY = hy + m * 0.75;
  ctx.strokeStyle = '#3c4048';
  ctx.lineWidth = Math.max(1.2, m * 0.045);
  ctx.setLineDash([m * 0.1, m * 0.06]);
  for (const side of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(hx, ringY);
    ctx.lineTo(lx + side * (LOAD.width / 2) * m * Math.cos(swing * 0.5) - 1, loadTop + 1);
    ctx.stroke();
  }
  ctx.setLineDash([]);
}

/** Euro pallet with clay blocks, bottom centre at (x, y); `squash` on landing. */
function drawLoad(ctx: CanvasRenderingContext2D, x: number, y: number, m: number, squash: number, alpha: number): void {
  ctx.save();
  ctx.globalAlpha *= alpha;
  ctx.translate(x, y);
  ctx.scale(1 + (1 - squash) * 0.6, squash);
  const W = LOAD.width * m;
  const P = LOAD.pallet * m;
  const B = LOAD.bricks * m;
  // pallet: boards and blocks
  ctx.fillStyle = '#b9864d';
  ctx.fillRect(-W / 2, -P, W, P * 0.35);
  ctx.fillStyle = '#8a5d2e';
  for (const bx of [-0.5, -0.06, 0.38]) ctx.fillRect(bx * W, -P * 0.65, W * 0.12, P * 0.65);
  ctx.fillStyle = '#c99a5f';
  ctx.fillRect(-W / 2, -P * 0.12, W, P * 0.12);
  // bricks: rows of red clay blocks
  const rows = 4;
  const cols = 3;
  for (let r = 0; r < rows; r++) {
    for (let col = 0; col < cols; col++) {
      const bw = (W * 0.94) / cols;
      const bh = B / rows;
      const bx = -W * 0.47 + col * bw + (r % 2 ? bw * 0.08 : 0);
      const by = -P - (r + 1) * bh;
      const g = ctx.createLinearGradient(0, by, 0, by + bh);
      g.addColorStop(0, '#d0603c');
      g.addColorStop(1, '#9c3f22');
      ctx.fillStyle = g;
      ctx.fillRect(bx + 0.8, by + 0.8, bw - 1.6 - (r % 2 && col === cols - 1 ? bw * 0.08 : 0), bh - 1.6);
      // holes of the clay block
      ctx.fillStyle = 'rgba(60, 20, 10, 0.35)';
      for (let k = 1; k <= 3; k++) ctx.fillRect(bx + (bw * k) / 4 - 1, by + bh * 0.3, 2, bh * 0.4);
    }
  }
  // strapping band
  ctx.fillStyle = 'rgba(240, 240, 240, 0.85)';
  ctx.fillRect(-W * 0.47, -P - B * 0.55, W * 0.94, Math.max(1.5, m * 0.04));
  ctx.restore();
}
